import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { SkeletonPathCategory } from '@/components/navigation/NavigationIntroScreen/components/SkeletonPathCategory';
import DeviceManager from '../util/deviceManager';
import { NavItemsSlider } from './search-bar/components/NavItemsSlider';
import { useCategoriesStyles } from './styles';
import { DefaultCategoryIcon } from './NavigationIntroScreen/components/Popup/Icons';

export const NavigationCategories = ({
  categories,
  handleSelectCategory,
  rtl,
  showSkeleton,
  mobilePosition,
}: {
  rtl?: boolean;
  categories: any[];
  handleSelectCategory: (category: { [key: string]: string | number }) => void;
  showSkeleton: boolean;
  mobilePosition: number;
}) => {
  const renderIcon = ({
    icon,
    iconColor,
    title,
    tourId,
  }: {
    icon: string | null;
    iconColor: string | null;
    title: string;
    tourId: number;
  }) => {
    if (!icon) {
      return <DefaultCategoryIcon style={{ width: '20px', height: '20px', color: iconColor }} />;
    }
    if (icon.endsWith('.webp')) {
      const iconSrc = `${process.env.NEXT_PUBLIC_CDN}/tours/${tourId}/navigation/icons/${icon}`;
      return <img width="20px" height="20px" src={iconSrc} alt={title} />;
    }
    const iconSrc = `${process.env.NEXT_PUBLIC_API}/v1/public/icons/${icon}?hex=${encodeURIComponent(iconColor)}`;
    return <img width="20px" height="20px" src={iconSrc} alt={title} />;
  };

  const filteredCategories = useMemo(
    () =>
      categories
        ?.filter((c) => !!c?.items?.length)
        .sort((a: { order: number }, b: { order: number }) => a.order - b.order),
    [categories],
  );
  const { categoriesGridWrapperStyle, gridItemInnerStyle, gridItemTitleStyle } =
    useCategoriesStyles();
  return (
    <>
      {(DeviceManager.isDesktop() ||
        (DeviceManager.isMobile() && (filteredCategories.length <= 3 || mobilePosition > 1))) && (
        <Grid
          id="categories-grid-container"
          container
          spacing="10px"
          justifyContent={{ lg: rtl ? 'end' : 'start' }}
          sx={{
            ...categoriesGridWrapperStyle,
          }}
        >
          {showSkeleton ? (
            <>
              <SkeletonPathCategory />
              <SkeletonPathCategory />
              <SkeletonPathCategory />
              <SkeletonPathCategory />
              <SkeletonPathCategory />
              <SkeletonPathCategory />
            </>
          ) : (
            filteredCategories.map((category) => {
              const hasItems = category.items?.length > 0;
              return (
                <Grid
                  item
                  // eslint-disable-next-line no-nested-ternary
                  xs={
                    DeviceManager.isMobile()
                      ? filteredCategories?.length === 1
                        ? 12
                        : filteredCategories?.length === 2
                        ? 6
                        : 4
                      : undefined
                  }
                  key={category.id}
                  onClick={() => handleSelectCategory(category)}
                >
                  <Stack
                    sx={{ ...gridItemInnerStyle, visibility: hasItems ? 'visibility' : 'hidden' }}
                  >
                    <Stack
                      width="30px"
                      height="30px"
                      sx={{
                        background: category.backgroundColor || '#2055FF',
                        borderRadius: '8px',
                        p: '6px 5px',
                      }}
                    >
                      {renderIcon(category)}
                    </Stack>
                    <Stack overflow="hidden">
                      <Typography sx={{ ...gridItemTitleStyle, direction: rtl ? 'rtl' : 'ltr' }}>
                        {category.title}
                      </Typography>
                    </Stack>
                  </Stack>
                </Grid>
              );
            })
          )}
        </Grid>
      )}
      {DeviceManager.isMobile() && filteredCategories.length > 3 && mobilePosition === 1 && (
        <NavItemsSlider
          categories={filteredCategories}
          showSkeleton={showSkeleton}
          handleItemSelect={handleSelectCategory}
          rtl={rtl}
          renderCategoryIcon={renderIcon}
        />
      )}
    </>
  );
};
